<script setup lang="ts">
/**
 * EN-TÊTE DE LA VITRINE — `/boutique/[slug]`.
 *
 * ⚠️ REMPLACE `BoutiqueHeaderCard` (carte blanche encartée), retiré du chantier
 * de refonte. La fiche produit a tranché la question du CARTONNAGE : un bloc
 * qui ne porte QUE de l'information (galerie, « Détails », « Vous aimerez
 * aussi ») flotte sur le fond de page ; la carte est réservée aux blocs
 * d'ACTION (panneau d'achat, rappel panier, livraison). L'en-tête d'une
 * boutique est de l'information pure — titre, vendeur, fenêtre de vente,
 * description — donc AUCUNE carte ici. Il ne restait sinon, sur la vitrine, que
 * des cartes blanches empilées sur un fond bleuté : rien ne hiérarchisait plus
 * rien, et la fiche produit disait le contraire à un clic de distance.
 *
 * ⚠️ LE TITRE EST PORTÉ ICI, PAS DANS LA BANNIÈRE. `BslugBanner` est le
 * composant PARTAGÉ des pages /b (billetterie) : il affiche une photo de
 * couverture et le logo de l'organisation en débord, sans jamais écrire le
 * titre par-dessus. On le garde tel quel — le modifier toucherait la
 * billetterie — et on lui laisse son rôle d'image d'ambiance. Le titre vit
 * juste dessous, sur fond de page, où il est lisible quelle que soit la photo
 * envoyée par l'association (une couverture claire avalerait un titre blanc,
 * un voile sombre pour compenser violerait `ui_no_bg_transparency`).
 */
import type { Shop } from "~/types/shop";
import { formatDateAssociation, fuseauDe } from "~/utils/heureAssociation";

const { t } = useI18n();

const props = defineProps<{
  shop: Shop;
  /** Description HTML DÉJÀ sanitizée par la page. */
  descriptionHtml: string;
  /** Nombre de produits affichés — repère de volume, façon marchand. */
  productCount: number;
}>();

defineEmits<{
  (e: "open-share"): void;
}>();

/** Jour de vente dans le fuseau de l'association (`timezone`, Paris par défaut). */
function formatDateFr(iso: string | null | undefined): string {
  return formatDateAssociation(iso, fuseauDe(props.shop), {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

const saleWindowLabel = computed(() => {
  const start = formatDateFr(props.shop.sale_start_date);
  const end = formatDateFr(props.shop.sale_end_date);
  if (start && end) return t("boutique.shopHero.venteDuAu", { debut: start, fin: end });
  if (start) return t("boutique.shopHero.venteAPartirDu", { debut: start });
  if (end) return t("boutique.shopHero.venteJusquAu", { fin: end });
  return "";
});

/** Nom affiché du vendeur (repli neutre si l'API n'a pas enrichi la boutique). */
/**
 * Description dépliée ou tronquée à 4 lignes.
 *
 * Le bouton est rendu INCONDITIONNELLEMENT quand il y a une description :
 * `line-clamp` ne dit pas au JS s'il a effectivement coupé, et le mesurer
 * demanderait un `scrollHeight` au montage — c'est-à-dire un rendu serveur qui
 * ne correspond pas au rendu client (le bouton apparaîtrait après hydratation).
 * Sur une description courte, « Lire la suite » ne fait que la re-rendre telle
 * quelle : pas de saut de page, pas de contenu masqué.
 */
const organizationLabel = computed(
  () => props.shop.organization_name || t("boutique.commun.associationGenerique"),
);

/**
 * Page publique de l'association — le lien n'apparaît QUE si
 * `organization_slug` existe (champ optionnel de l'API), sinon le nom est
 * rendu en texte simple plutôt qu'en lien mort.
 */
const localePath = useLocalePath();
const organizationUrl = computed<string | null>(() =>
  props.shop.organization_slug
    ? localePath({ path: `/associations/${props.shop.organization_slug}` })
    : null,
);

const descriptionExpanded = ref(false);

/**
 * Le bouton n'apparaît QUE si le texte est réellement coupé.
 *
 * `line-clamp` ne prévient pas le JS qu'il a tronqué : on mesure donc après
 * rendu (hauteur du contenu > hauteur visible). Sur une description courte,
 * « Lire la suite » ne déroulait rien — un bouton qui ne fait rien.
 *
 * La mesure est refaite au redimensionnement : une description tient sur 4
 * lignes en grand écran et déborde en étroit.
 */
const descriptionRef = ref<HTMLElement | null>(null);
const isTruncated = ref(false);

function measureTruncation() {
  const el = descriptionRef.value;
  if (!el || descriptionExpanded.value) return;
  isTruncated.value = el.scrollHeight > el.clientHeight + 1;
}

onMounted(() => {
  void nextTick(measureTruncation);
  window.addEventListener("resize", measureTruncation);
});
onBeforeUnmount(() => window.removeEventListener("resize", measureTruncation));

</script>

<template>
  <header>
    <!--
      Titre + vendeur + partage sur UNE MÊME LIGNE (Karim 2026-08-09) : le
      vendeur et le partage flottaient dans la colonne de droite, loin du nom
      de la boutique qu'ils qualifient. `flex-wrap` : sur mobile, le partage
      passe sous le titre plutôt que de l'écraser.
      Le PANIER, lui, reste dans la carte du récapitulatif — c'est une action
      de commande, pas d'identité.
    -->
    <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div class="min-w-0">
        <h1 class="text-2xl md:text-3xl lg:text-4xl font-bold text-[#2d2e5c] dark:text-white leading-tight text-balance">
          {{ shop.title }}
        </h1>
        <p class="text-xs md:text-sm text-[#717198] dark:text-gray-400 mt-1">
          {{ $t("boutique.commun.par") }}
          <a
            v-if="organizationUrl"
            :href="organizationUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="font-bold hover:underline"
            :style="{ color: 'var(--accent, #212121)' }"
          >{{ organizationLabel }}</a>
          <span v-else class="font-bold text-[#505870] dark:text-gray-300">{{ organizationLabel }}</span>
        </p>
      </div>

      <button
        type="button"
        class="shrink-0 inline-flex items-center justify-center gap-1.5 min-h-11 px-3 md:px-4 rounded-2xl border border-gray-200 dark:border-[#262626] bg-white dark:bg-[#141414] text-xs md:text-sm font-semibold text-[#505870] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#1e1e1e] transition-colors"
        :aria-label="$t('boutique.commun.partagerLaBoutique')"
        @click="$emit('open-share')"
      >
        <UIcon name="i-lucide-share-2" class="size-4" />
        <span class="hidden sm:inline">{{ $t("commun.actions.partager") }}</span>
      </button>
    </div>

    <!-- Repères secondaires sur UNE ligne : volume de l'offre + fenêtre de
         vente. Ils étaient auparavant empilés en pleine largeur dans la carte,
         où la fenêtre de vente pesait autant que le titre. En texte fin sous le
         titre, ils informent sans réclamer l'attention. -->
    <div
      v-if="saleWindowLabel"
      class="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-3 text-xs md:text-sm text-[#717198] dark:text-gray-400"
    >
      <span class="inline-flex items-center gap-1.5 font-medium">
        <UIcon name="i-lucide-calendar-days" class="size-4 shrink-0" />
        {{ saleWindowLabel }}
      </span>
    </div>

    <!-- Description : largeur de LECTURE limitée (`max-w-2xl`). En pleine
         largeur sur un écran 1280px, une description d'association se lit sur
         des lignes de 150 caractères — illisible.
         ⚠️ PAS `PublicPageDescriptionExpandable` ici : ce composant PARTAGÉ
         (/a, /b) dégrade sa troncature avec un dégradé `from-white`, calibré
         pour un texte posé DANS une carte blanche. Sur le fond de page bleuté
         de la vitrine, ce dégradé peint une bavure blanche au-dessus du texte.
         Le corriger dans le composant partagé toucherait les pages don et
         billetterie, hors périmètre. On replie donc ici sur une troncature par
         `line-clamp`, purement CSS : aucun dégradé, donc aucune hypothèse sur
         la couleur du fond, et aucune transparence introduite. -->
    <div v-if="shop.description" class="mt-4 max-w-2xl">
      <!-- eslint-disable vue/no-v-html -->
      <!-- Reason: HTML sanitizé par la page avant d'être passé en prop -->
      <div
        ref="descriptionRef"
        class="text-sm md:text-base leading-relaxed text-[#505870] dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none"
        :class="descriptionExpanded ? '' : 'line-clamp-4'"
        v-html="descriptionHtml"
      />
      <!-- eslint-enable vue/no-v-html -->
      <button
        v-if="isTruncated || descriptionExpanded"
        type="button"
        class="inline-flex items-center gap-1.5 mt-2 min-h-11 text-sm font-semibold text-[#2d2e5c] dark:text-white hover:underline"
        @click="descriptionExpanded = !descriptionExpanded"
      >
        {{ descriptionExpanded ? $t("boutique.shopHero.reduire") : $t("boutique.shopHero.lireLaSuite") }}
        <UIcon
          :name="descriptionExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          class="size-4"
        />
      </button>
    </div>
  </header>
</template>
